import httpService from './httpService';
import {
  PersonalEvent as Event,
  Event as CommunityEvent,
  EventParticipationAnswer,
  CreateEventModel,
  UpdateEventModel,
  RecurrenceEditType,
} from '../types/event';
import { store } from '../redux/store';
import {
  setEvents,
  updateEvent as updateEventRedux,
  setAttendance as setAttendanceRedux,
} from '../redux/slices/eventSlice';

const addDateToEvent = (event: any): any => {
  const { startTime, endTime, deadline, dateOptions } = event;
  return {
    ...event,
    startTime: new Date(startTime),
    endTime: endTime ? new Date(endTime) : undefined,
    deadline: deadline ? new Date(deadline) : undefined,
    dateOptions: dateOptions?.map((o: any) => ({
      ...o,
      startTime: new Date(o.startTime),
      endTime: o.endTime ? new Date(o.endTime) : undefined,
    })),
  };
};

export async function getEventWithToken(token: string, type?: 'external-invite' | 'event-invite'): Promise<Event> {
  const response = await httpService.get(`/events/invite/${token}?type=${type}`);
  return response.data;
}

export async function getEvent(eventId: number): Promise<Event> {
  const response = await httpService.get(`/events/${eventId}`);
  return response.data;
}

export async function respondToEvent(
  eventId: string,
  answer: EventParticipationAnswer,
  description = '',
): Promise<void> {
  const response = await httpService.post(`/events/${eventId}/invite/respond`, { answer, description });
  return response.data;
}

export async function respondToEventPollBulk(
  eventId: string,
  responses: { [customFieldId: string]: string },
): Promise<boolean> {
  const response = await httpService.post(`/events/${eventId}/polls/respond-bulk`, { responses });
  return response.data;
}

export async function respondToEventDateOptionBulk(
  eventId: string,
  dateOptions: { dateOptionId: number; answer: EventParticipationAnswer; description?: string }[],
): Promise<boolean> {
  const response = await httpService.post(`/events/${eventId}/date-options/respond-bulk`, { dateOptions });
  return response.data;
}

export async function getEventInviteWithToken(token: string): Promise<Event> {
  const response = await httpService.get(`/events/invite/${token}`);
  return response.data;
}

export async function issueEventTicketUnit(eventId: number): Promise<{ veraId: string }> {
  return httpService
    .get<{ veraId: string }>(`/events/${eventId}/issue-ticket`)
    .then((res: { data: { veraId: string } }) => res.data);
}

export async function getMyTicketCode(
  eventId: number,
): Promise<{ personalCode?: string | null; veraLink?: string; status?: string } | null> {
  const response = await httpService.get(`/events/${eventId}/ticket-code`);
  return response.data;
}

/**
 * Get all upcoming events for the current user
 */
export async function getMyUpcomingEvents(): Promise<Event[]> {
  const response = await httpService.get('/events/@me/upcoming');
  return response.data;
}

/**
 * Get all past events for the current user
 */
export async function getMyPastEvents(): Promise<Event[]> {
  const response = await httpService.get('/events/@me/past');
  return response.data;
}

/**
 * Get all events for the current user (both upcoming and past)
 */
export async function getMyEvents(): Promise<Event[]> {
  const [upcoming, past] = await Promise.all([getMyUpcomingEvents(), getMyPastEvents()]);
  return [...upcoming, ...past];
}

// Community-specific event functions
export async function getCommunityUpcomingEvents(communityId: number): Promise<CommunityEvent[]> {
  const { data } = await httpService.get<any[]>(`/networks/communities/${communityId}/events/upcoming`);
  const events = data.map(addDateToEvent);
  store.dispatch(setEvents({ communityId, events }));
  return events;
}

export async function getCommunityPastEvents(communityId: number): Promise<CommunityEvent[]> {
  const { data } = await httpService.get<any[]>(`/networks/communities/${communityId}/events/past`);
  return data.map(addDateToEvent);
}

export async function fetchCommunityEvent(eventId: number, communityId: number): Promise<CommunityEvent> {
  const { data } = await httpService.get<any>(`/events/${eventId}`);
  const transformed = addDateToEvent(data);
  store.dispatch(updateEventRedux({ communityId, event: transformed }));
  return transformed;
}

export async function setEventAttendance(
  eventId: number,
  participantId: number,
  answer: any,
  communityId: number,
): Promise<void> {
  await httpService.put(`/events/${eventId}/participants/${participantId}/attendance`, { answer });
  store.dispatch(setAttendanceRedux({ communityId, participantId, answer }));
}

export async function generateEventInviteLink(eventId: number): Promise<string> {
  const { data } = await httpService.post(`/events/${eventId}/invite-link`);
  return data.invite;
}

export async function createEvent(communityId: number, eventData: CreateEventModel): Promise<CommunityEvent> {
  const { data } = await httpService.post(`/networks/communities/${communityId}/events`, eventData);
  return addDateToEvent(data);
}

export async function updateEvent(
  eventId: number,
  updateModel: UpdateEventModel,
  recurrenceEditType?: RecurrenceEditType,
): Promise<CommunityEvent> {
  const { data } = await httpService.put(`/events/${eventId}`, { ...updateModel, recurrenceEditType });
  const transformed = addDateToEvent(data);
  try {
    const { communityId } = transformed as any;
    if (communityId) {
      store.dispatch(updateEventRedux({ communityId, event: transformed }));
    }
  } catch (err) {
    // ignore redux dispatch errors
  }
  return transformed;
}
